import type { Toast } from '@nuxt/ui/composables';
import { useToast } from '@nuxt/ui/composables';
import { useTimeoutFn } from '@vueuse/core';
import { onScopeDispose } from 'vue';

/**
 * Everything a caller supplies for the singleton toast. The primitive owns `id`
 * and `onUpdate:open` (it injects its own to detect a user-close), so both are
 * omitted from what callers pass.
 */
export type SingletonToastContent = Omit<Partial<Toast>, 'id' | 'onUpdate:open'>;

interface UseSingletonToastOptions {
	/**
	 * Called when the *user* dismisses the toast (clicks close). Not called when
	 * `sync(null)` closes it programmatically — that path is a state change, not
	 * a dismissal.
	 */
	onUserClose?: () => void;
}

interface SingletonToastController {
	/** Show the toast (add), morph it in place (update), or close it (`null`). */
	sync: (content: SingletonToastContent | null) => void;
}

/**
 * Nuxt UI's `remove(id)` flips `open:false`, then splices the toast out of state
 * after a hardcoded 200ms (see `@nuxt/ui/.../composables/useToast.js`). That
 * timer is unconditional: a re-`add` inside the window is merged into the still
 * present (closing) entry and then deleted by the pending splice, so the toast
 * silently vanishes. We wait out the window with a small buffer — so the splice
 * always runs first — before re-adding, and gate re-adds behind a `closing`
 * state so a fast hide→show flap can never resurrect-then-lose the toast.
 */
const REMOVAL_SETTLE_MS = 250;

type State = 'idle' | 'shown' | 'closing';

/**
 * Owns the lifecycle of a single, persistent toast identified by `id`, keeping
 * it in sync with a desired content value while shielding callers from Nuxt UI's
 * add/update/remove timing hazards (the 200ms removal window, the `update()`
 * `_updated` guard, and user-close desync). Callers describe *what* the toast
 * should say via `sync()`; this primitive decides add vs update vs remove. Drive
 * `sync()` from a reactive effect (watch/watchEffect): it coalesces to one call
 * per flush, which keeps an `update()` and a `remove()` out of the same tick.
 */
export function useSingletonToast(id: string, options: UseSingletonToastOptions = {}): SingletonToastController {
	const toast = useToast();

	let state: State = 'idle';
	let desired: SingletonToastContent | null = null;

	const { start: startSettle, stop: stopSettle } = useTimeoutFn(onSettled, REMOVAL_SETTLE_MS, { immediate: false });

	function sync(content: SingletonToastContent | null) {
		desired = content;
		reconcile();
	}

	function reconcile() {
		// Mid-removal: hold until the window settles; onSettled() re-reconciles.
		if (state === 'closing') return;

		if (desired === null) {
			if (state === 'shown') beginClose();
			return;
		}

		if (state === 'shown' && toast.toasts.value.some((entry) => entry.id === id)) {
			toast.update(id, withOwnership(desired));
			return;
		}

		state = 'shown';
		toast.add({ id, ...withOwnership(desired) });
	}

	function beginClose() {
		toast.remove(id);
		state = 'closing';
		startSettle();
	}

	function onSettled() {
		state = 'idle';
		// Re-show if a `sync(content)` landed during the removal window.
		reconcile();
	}

	function onOpenChange(open: boolean) {
		if (open || state !== 'shown') return;
		// User closed the toast. Default to not fighting the user — a consumer that
		// still wants it will `sync()` again. Drive the removal ourselves (rather
		// than trusting the Toaster) so the primitive owns its full lifecycle;
		// remove-by-id is idempotent if the Toaster also tears the toast down.
		desired = null;
		beginClose();
		options.onUserClose?.();
	}

	function withOwnership(content: SingletonToastContent): Omit<Partial<Toast>, 'id'> {
		return { ...content, 'onUpdate:open': onOpenChange };
	}

	onScopeDispose(() => {
		stopSettle();
		if (state === 'shown') toast.remove(id);
		state = 'idle';
	});

	return { sync };
}
